import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { API_URL } from '../utils/Functions';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Reply states
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/contacts`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      const res = await response.json();
      if (!response.ok) throw res;
      
      let parsedContacts = [];
      if (Array.isArray(res)) parsedContacts = res;
      else if (res.data && Array.isArray(res.data.data)) parsedContacts = res.data.data;
      else if (res.data && Array.isArray(res.data)) parsedContacts = res.data;
      
      setContacts(parsedContacts);
    } catch (err) {
      setError('Failed to load contacts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleOpenView = (item) => {
    setSelectedContact(item);
    setReplyMessage('');
    setShowModal(true);
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim() || !selectedContact?.email) return;
    
    setSendingReply(true);
    setError('');
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/contacts/${selectedContact.id}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reply_message: replyMessage })
      });

      const res = await response.json();
      if (!response.ok) throw res;

      const updatedContact = res.data;
      setContacts(prev => prev.map(c => c.id === updatedContact.id ? updatedContact : c));
      setSelectedContact(updatedContact);
      
      toast.success('Reply sent successfully!');
      setReplyMessage('');
    } catch (err) {
      toast.error(err.message || 'Failed to send reply.');
    } finally {
      setSendingReply(false);
    }
  };

  const executeDelete = async () => {
    if (!itemToDelete) return;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/contacts/${itemToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to delete');
      setContacts(contacts.filter(c => c.id !== itemToDelete.id));
      setItemToDelete(null);
      toast.success('Message deleted.');
      if (selectedContact && selectedContact.id === itemToDelete.id) {
          setShowModal(false);
      }
    } catch (err) {
      toast.error('Error deleting message.');
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return {
      date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const filteredContacts = contacts.filter(contact => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (contact.full_name && contact.full_name.toLowerCase().includes(term)) ||
      (contact.email && contact.email.toLowerCase().includes(term)) ||
      (contact.message && contact.message.toLowerCase().includes(term))
    );
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .ct-wrap { font-family: 'Inter', sans-serif; }

        /* Hero Banner */
        .ct-hero {
          background: linear-gradient(135deg, #040f1f 0%, #061830 40%, #CFA047 100%);
          border-radius: 18px; padding: 32px 36px; margin-bottom: 28px;
          display: flex; align-items: center; justify-content: space-between;
          position: relative; overflow: hidden;
        }
        .ct-hero::before { content: ''; position: absolute; top: -40px; right: -40px; width: 240px; height: 240px; border-radius: 50%; background: rgba(255,255,255,0.05); }
        .ct-hero::after { content: ''; position: absolute; bottom: -60px; right: 120px; width: 180px; height: 180px; border-radius: 50%; background: rgba(255,255,255,0.04); }
        .ct-hero-left { position: relative; z-index: 1; }
        .ct-hero-eyebrow { font-size: 11px; font-weight: 700; color: #CFA047; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
        .ct-hero-title { font-size: 26px; font-weight: 800; color: #ffffff; margin: 0 0 8px; line-height: 1.2; }
        .ct-hero-sub { font-size: 14px; color: rgba(255,255,255,0.8); margin: 0; }
        .ct-hero-right { position: relative; z-index: 1; display: flex; align-items: center; gap: 16px; }
        .ct-hero-stat { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.15); border-radius: 14px; padding: 16px 22px; text-align: center; backdrop-filter: blur(6px); }
        .ct-hero-stat-val { font-size: 28px; font-weight: 800; color: #ffffff; line-height: 1; }
        .ct-hero-stat-label { font-size: 11px; color: #CFA047; font-weight: 600; margin-top: 4px; }

        /* Error & Success */
        .ct-error { background: #fff1f2; border: 1px solid #fecdd3; border-radius: 10px; padding: 12px 16px; color: #e11d48; font-size: 13px; margin-bottom: 20px; display: flex; align-items: center; gap: 10px; }
        .ct-success { background: #fdf5e6; border: 1px solid #fce5b2; border-radius: 10px; padding: 12px 16px; color: #a67f35; font-size: 13px; margin-bottom: 20px; display: flex; align-items: center; gap: 10px; }

        /* Section Header */
        .ct-section-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; flex-wrap: wrap; gap: 12px; }
        .ct-section-label { font-size: 13px; font-weight: 700; color: #5a6a7e; display: flex; align-items: center; gap: 8px; flex: 1; min-width: 200px; }
        .ct-section-label::after { content: ''; flex: 1; height: 1px; background: #eef1f5; }
        .ct-count-badge { background: #f0f4f8; color: #5a6a7e; font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 20px; margin-left: 8px; }
        .ct-search-box { position: relative; width: 280px; }
        .ct-search-box i { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #9babbf; font-size: 13px; }
        .ct-search-box input { width: 100%; padding: 10px 14px 10px 38px; border-radius: 10px; border: 1.5px solid #dde3ea; font-size: 13px; font-family: 'Inter', sans-serif; color: #2d3a4b; outline: none; transition: all 0.2s; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.02); }
        .ct-search-box input:focus { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207, 160, 71,0.12); }

        /* Grid Layout for Cards */
        .ct-grid-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 20px; padding-bottom: 24px; }
        .ct-msg-card { background: #fff; border-radius: 14px; border: 1px solid #dde3ea; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.05); transition: all 0.22s; display: flex; flex-direction: column; cursor: pointer; }
        .ct-msg-card:hover { box-shadow: 0 8px 24px rgba(0,0,0,0.1); transform: translateY(-3px); border-color: #CFA047; }
        .ct-card-header { padding: 16px; display: flex; align-items: center; gap: 14px; border-bottom: 1px solid #f1f5f9; }
        .ct-card-avatar { width: 44px; height: 44px; border-radius: 50%; background: linear-gradient(135deg, #f0f4f8, #e2e8f0); display: flex; align-items: center; justify-content: center; color: #64748b; font-size: 18px; border: 1px solid #dde3ea; flex-shrink: 0; font-weight: 700; }
        .ct-card-client-info { flex: 1; overflow: hidden; }
        .ct-card-name { font-weight: 700; color: #1a2332; font-size: 15px; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ct-card-email { font-size: 12px; color: #8496a9; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ct-card-body { padding: 16px; flex: 1; }
        .ct-card-msg { font-size: 13.5px; color: #475569; line-height: 1.6; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
        .ct-card-footer { padding: 12px 16px; border-top: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; background: #fafbfc; }
        .ct-date { font-size: 11px; color: #94a3b8; font-weight: 600; display: flex; align-items: center; gap: 6px; }
        .ct-actions { display: flex; gap: 6px; }
        .ct-act-btn { display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 8px; border: 1.5px solid #dde3ea; background: #fff; color: #6b7a90; cursor: pointer; font-size: 13px; transition: all 0.18s; }
        .ct-act-btn:hover { background: #f8fafc; border-color: #bcc7d4; color: #1a2332; }
        .ct-act-btn.view:hover { border-color: #CFA047; color: #CFA047; background: #fffbf2; }
        .ct-act-btn.del:hover { border-color: #fca5a5; color: #ef4444; background: #fff1f2; }

        /* Empty state */
        .ct-empty { text-align: center; padding: 70px 24px; }
        .ct-empty-icon-wrap { width: 88px; height: 88px; border-radius: 24px; background: linear-gradient(135deg, #f8fafc, #fdf5e6); display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; box-shadow: 0 8px 24px rgba(207, 160, 71,0.15); }
        .ct-empty-icon-wrap i { font-size: 38px; color: #CFA047; }
        .ct-empty-title { font-size: 18px; font-weight: 700; color: #2d3a4b; margin-bottom: 8px; }
        .ct-empty-text { font-size: 14px; color: #9babbf; max-width: 300px; margin: 0 auto 24px; line-height: 1.6; }

        /* Skeleton Loading Cards */
        .ct-skel-card { border-radius: 14px; border: 1px solid #dde3ea; overflow: hidden; background: #fff; }
        .ct-skel-header { padding: 16px; display: flex; gap: 14px; border-bottom: 1px solid #f1f5f9; }
        .ct-skel-avatar { width: 44px; height: 44px; border-radius: 50%; background: linear-gradient(90deg, #f0f4f8 25%, #e4eaf1 50%, #f0f4f8 75%); background-size: 200% 100%; animation: ct-shimmer 1.4s infinite; }
        .ct-skel-lines { flex: 1; display: flex; flex-direction: column; gap: 8px; justify-content: center; }
        .ct-skel-line { height: 12px; border-radius: 6px; background: linear-gradient(90deg, #f0f4f8 25%, #e4eaf1 50%, #f0f4f8 75%); background-size: 200% 100%; animation: ct-shimmer 1.4s infinite; }
        .ct-skel-body { padding: 16px; display: flex; flex-direction: column; gap: 10px; }
        @keyframes ct-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

        /* Slide-in Drawer */
        .ct-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); backdrop-filter: blur(4px); z-index: 900; display: flex; justify-content: flex-end; }
        @keyframes ct-drop { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .ct-drawer { width: 900px; max-width: 95vw; background: #fff; height: 100vh; display: flex; flex-direction: column; box-shadow: -8px 0 40px rgba(0,0,0,0.18); animation: ct-drop 0.28s cubic-bezier(0.22,1,0.36,1); }
        .ct-dh { padding: 22px 28px; border-bottom: 1px solid #eef1f5; display: flex; align-items: center; justify-content: space-between; background: linear-gradient(135deg, #061830, #040f1f); flex-shrink: 0; }
        .ct-dh-left { display: flex; align-items: center; gap: 14px; }
        .ct-dh-icon { width: 40px; height: 40px; border-radius: 12px; background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25); display: flex; align-items: center; justify-content: center; color: #CFA047; font-size: 16px; }
        .ct-dh-title { font-size: 17px; font-weight: 700; color: #fff; margin: 0; }
        .ct-dh-sub { font-size: 12px; color: rgba(255,255,255,0.8); margin-top: 2px; }
        .ct-dh-actions button { background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.2); color: white; width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 14px; transition: background 0.18s; }
        .ct-dh-actions button:hover { background: rgba(255,255,255,0.22); }
        
        .ct-drawer-body { display: flex; flex: 1; overflow: hidden; }

        /* Left: Message Panel */
        .ct-msg-panel { width: 400px; background: #f8fafc; border-right: 1px solid #dde3ea; display: flex; flex-direction: column; overflow-y: auto; }
        .ct-sender-box { padding: 32px 28px 24px; border-bottom: 1px solid #dde3ea; background: white; }
        .ct-sender-avatar { width: 64px; height: 64px; border-radius: 50%; background: #061830; color: #CFA047; display: flex; align-items: center; justify-content: center; font-size: 24px; font-weight: 700; margin-bottom: 16px; box-shadow: 0 4px 12px rgba(6,24,48,0.15); }
        .ct-sender-name { font-size: 20px; font-weight: 800; color: #0f172a; margin: 0 0 4px; }
        .ct-sender-meta { display: flex; flex-direction: column; gap: 6px; margin-top: 12px; }
        .ct-sender-meta-item { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #64748b; }
        .ct-sender-meta-item i { color: #94a3b8; width: 14px; }
        
        .ct-message-box { padding: 28px; flex: 1; display: flex; flex-direction: column; }
        .ct-message-title { font-size: 11px; font-weight: 700; color: #CFA047; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 14px; display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
        .ct-message-title::after { content: ''; flex: 1; height: 1px; background: #dde3ea; }
        
        .ct-thread { display: flex; flex-direction: column; gap: 16px; overflow-y: auto; padding-right: 8px; flex: 1; }
        .ct-bubble { padding: 16px 20px; border-radius: 14px; max-width: 90%; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
        .ct-bubble-header { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 8px; gap: 12px; }
        .ct-bubble-name { font-size: 13px; font-weight: 700; color: #0f172a; }
        .ct-bubble-time { font-size: 11px; color: #94a3b8; }
        .ct-bubble-text { font-size: 14.5px; line-height: 1.6; white-space: pre-wrap; word-break: break-word; }
        
        .ct-bubble-user { background: white; border: 1px solid #dde3ea; align-self: flex-start; border-top-left-radius: 4px; }
        .ct-bubble-user .ct-bubble-text { color: #334155; }
        
        .ct-bubble-admin { background: #fdf5e6; border: 1px solid #fce5b2; align-self: flex-end; border-top-right-radius: 4px; }
        .ct-bubble-admin .ct-bubble-text { color: #8a6422; }
        .ct-bubble-admin .ct-bubble-name { color: #a67f35; }

        /* Right: Reply Panel */
        .ct-reply-panel { flex: 1; padding: 32px 40px; overflow-y: auto; background: #fff; display: flex; flex-direction: column; }
        .ct-reply-notice { background: #fdf5e6; border: 1px solid #fce5b2; border-radius: 12px; padding: 16px; display: flex; gap: 12px; margin-bottom: 24px; }
        .ct-reply-notice i { color: #CFA047; font-size: 20px; margin-top: 2px; }
        .ct-reply-notice-text { font-size: 13.5px; color: #a67f35; line-height: 1.5; }
        
        .ct-float-group { position: relative; margin-bottom: 16px; flex: 1; display: flex; flex-direction: column; }
        .ct-float-textarea { flex: 1; width: 100%; padding: 24px 20px; border-radius: 12px; border: 1.5px solid #dde3ea; font-size: 15px; font-family: 'Inter', sans-serif; color: #2d3a4b; outline: none; background: #f9fbfc; transition: border-color 0.18s, box-shadow 0.18s; box-sizing: border-box; resize: none; line-height: 1.6; }
        .ct-float-textarea:focus { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207, 160, 71,0.1); background: #fff; }
        .ct-float-textarea:focus + .ct-float-label, .ct-float-textarea:not(:placeholder-shown) + .ct-float-label { top: 9px; font-size: 11px; color: #CFA047; font-weight: 700; }
        .ct-float-label { position: absolute; top: 24px; left: 20px; font-size: 14px; color: #9babbf; font-weight: 500; pointer-events: none; transition: all 0.18s; }

        .ct-df { padding: 18px 28px; border-top: 1px solid #eef1f5; display: flex; justify-content: space-between; align-items: center; background: #f9fbfc; flex-shrink: 0; }
        .ct-df-btns { display: flex; gap: 12px; margin-left: auto; }
        .ct-btn-cancel { padding: 10px 22px; border-radius: 9px; font-weight: 600; cursor: pointer; background: white; border: 1.5px solid #dde3ea; color: #4a5568; font-family: 'Inter', sans-serif; font-size: 13px; transition: all 0.18s; }
        .ct-btn-cancel:hover { background: #f7f9fc; }
        .ct-btn-save { padding: 10px 26px; border-radius: 9px; font-weight: 600; cursor: pointer; background: linear-gradient(135deg, #CFA047 0%, #061830 100%); border: none; color: white; font-family: 'Inter', sans-serif; font-size: 13px; box-shadow: 0 2px 8px rgba(207, 160, 71,0.3); transition: all 0.18s; display: flex; align-items: center; gap: 8px; }
        .ct-btn-save:hover:not(:disabled) { box-shadow: 0 4px 14px rgba(207, 160, 71,0.4); transform: translateY(-1px); }
        .ct-btn-save:disabled { opacity: 0.7; cursor: not-allowed; }

        /* Delete Confirm */
        .ct-del-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.6); backdrop-filter: blur(4px); display: flex; justify-content: center; align-items: center; z-index: 999; }
        @keyframes ct-slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>

      <div className="ct-wrap">
        {/* Hero Banner */}
        <div className="ct-hero">
          <div className="ct-hero-left">
            <div className="ct-hero-eyebrow"><i className="fa-solid fa-address-book"></i> Contacts</div>
            <h2 className="ct-hero-title">Consultation Requests</h2>
            <p className="ct-hero-sub">Manage and reply to user inquiries submitted through the contact forms.</p>
          </div>
          <div className="ct-hero-right">
            {!loading && (
              <>
                <div className="ct-hero-stat">
                  <div className="ct-hero-stat-val">{contacts.length}</div>
                  <div className="ct-hero-stat-label">Total Messages</div>
                </div>
              </>
            )}
          </div>
        </div>

        {error && <div className="ct-error"><i className="fa-solid fa-circle-exclamation"></i>{error}</div>}

        <div className="ct-section-header">
          <div className="ct-section-label">
            <i className="fa-solid fa-envelope-open-text" style={{ color: '#CFA047' }}></i>
            Inbox
            <span className="ct-count-badge">{filteredContacts.length}</span>
          </div>
          <div className="ct-search-box">
            <i className="fa-solid fa-magnifying-glass"></i>
            <input 
              type="text" 
              placeholder="Search messages by name, email..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
            />
          </div>
        </div>

        {loading ? (
          <div className="ct-grid-list">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="ct-skel-card">
                <div className="ct-skel-header">
                  <div className="ct-skel-avatar"></div>
                  <div className="ct-skel-lines">
                    <div className="ct-skel-line" style={{ width: '60%' }}></div>
                    <div className="ct-skel-line" style={{ width: '40%' }}></div>
                  </div>
                </div>
                <div className="ct-skel-body">
                  <div className="ct-skel-line" style={{ width: '90%' }}></div>
                  <div className="ct-skel-line" style={{ width: '95%' }}></div>
                  <div className="ct-skel-line" style={{ width: '70%' }}></div>
                </div>
              </div>
            ))}
          </div>
        ) : contacts.length === 0 ? (
          <div className="ct-empty">
            <div className="ct-empty-icon-wrap"><i className="fa-solid fa-inbox"></i></div>
            <div className="ct-empty-title">Inbox Zero!</div>
            <div className="ct-empty-text">You have no consultation requests or contact messages at the moment. Check back later!</div>
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="ct-empty" style={{ padding: '50px 24px' }}>
            <div className="ct-empty-icon-wrap" style={{ width: 72, height: 72, margin: '0 auto 16px' }}>
              <i className="fa-solid fa-magnifying-glass" style={{ fontSize: 28 }}></i>
            </div>
            <div className="ct-empty-title">No matching messages</div>
            <div className="ct-empty-text" style={{ maxWidth: 350 }}>We couldn't find any messages matching "<strong>{searchTerm}</strong>".</div>
            <button onClick={() => setSearchTerm('')} style={{ background: '#fff', border: '1px solid #dde3ea', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontWeight: 600, color: '#475569', marginTop: '8px', display: 'inline-block' }}>Clear Search</button>
          </div>
        ) : (
          <div className="ct-grid-list">
            {filteredContacts.map(item => {
              const dt = formatDate(item.created_at);
              return (
                <div key={item.id} className="ct-msg-card" onClick={() => handleOpenView(item)}>
                  <div className="ct-card-header">
                    <div className="ct-card-avatar">
                      {item.full_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="ct-card-client-info">
                      <div className="ct-card-name">{item.full_name}</div>
                      <div className="ct-card-email">{item.email || 'No email provided'}</div>
                    </div>
                  </div>
                  <div className="ct-card-body">
                    <div className="ct-card-msg">{item.message}</div>
                  </div>
                  <div className="ct-card-footer" onClick={e => e.stopPropagation()}>
                    <span className="ct-date"><i className="fa-regular fa-clock"></i> {dt.date} {dt.time}</span>
                    <div className="ct-actions">
                      <button className="ct-act-btn view" onClick={() => handleOpenView(item)} title="Read & Reply">
                        <i className="fa-solid fa-reply"></i>
                      </button>
                      <button className="ct-act-btn del" onClick={() => setItemToDelete(item)} title="Delete">
                        <i className="fa-solid fa-trash-can"></i>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Slide-in Drawer */}
      {showModal && selectedContact && (
        <div className="ct-overlay" onClick={() => setShowModal(false)}>
          <div className="ct-drawer" onClick={e => e.stopPropagation()}>
            <div className="ct-dh">
              <div className="ct-dh-left">
                <div className="ct-dh-icon"><i className="fa-solid fa-envelope-open-text"></i></div>
                <div>
                  <div className="ct-dh-title">Message Details</div>
                  <div className="ct-dh-sub">View request and send a direct reply via email.</div>
                </div>
              </div>
              <div className="ct-dh-actions">
                <button onClick={() => setShowModal(false)}><i className="fa-solid fa-xmark"></i></button>
              </div>
            </div>

            <div className="ct-drawer-body">
              {/* Left: Message Panel */}
              <div className="ct-msg-panel">
                <div className="ct-sender-box">
                  <div className="ct-sender-avatar">
                    {selectedContact.full_name.charAt(0).toUpperCase()}
                  </div>
                  <h3 className="ct-sender-name">{selectedContact.full_name}</h3>
                  <div className="ct-sender-meta">
                    <div className="ct-sender-meta-item">
                      <i className="fa-solid fa-envelope"></i> {selectedContact.email || <span style={{fontStyle:'italic', color:'#cbd5e1'}}>Not provided</span>}
                    </div>
                    <div className="ct-sender-meta-item">
                      <i className="fa-solid fa-phone"></i> {selectedContact.phone || <span style={{fontStyle:'italic', color:'#cbd5e1'}}>Not provided</span>}
                    </div>
                    <div className="ct-sender-meta-item" style={{ marginTop: '4px' }}>
                      <i className="fa-regular fa-calendar"></i> {new Date(selectedContact.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="ct-message-box">
                  <div className="ct-message-title"><i className="fa-solid fa-comments"></i> Conversation Thread</div>
                  <div className="ct-thread">
                    <div className="ct-bubble ct-bubble-user">
                      <div className="ct-bubble-header">
                        <span className="ct-bubble-name">{selectedContact.full_name}</span>
                        <span className="ct-bubble-time">{new Date(selectedContact.created_at).toLocaleString()}</span>
                      </div>
                      <div className="ct-bubble-text">{selectedContact.message}</div>
                    </div>

                    {selectedContact.replies && selectedContact.replies.map((reply, idx) => (
                      <div key={idx} className="ct-bubble ct-bubble-admin">
                        <div className="ct-bubble-header">
                          <span className="ct-bubble-name">{reply.sender || 'Admin'}</span>
                          <span className="ct-bubble-time">{new Date(reply.sent_at).toLocaleString()}</span>
                        </div>
                        <div className="ct-bubble-text">{reply.message}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: Reply Panel */}
              <form className="ct-reply-panel" onSubmit={handleReplySubmit}>
                <div className="ct-section-title" style={{ fontSize: '11px', fontWeight: 700, color: '#CFA047', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fa-solid fa-paper-plane"></i> Send Reply
                  <div style={{ flex: 1, height: '1px', background: '#fdf5e6' }}></div>
                </div>

                {!selectedContact.email ? (
                  <div className="ct-reply-notice" style={{ background: '#fef2f2', borderColor: '#fca5a5' }}>
                    <i className="fa-solid fa-triangle-exclamation" style={{ color: '#ef4444' }}></i>
                    <div className="ct-reply-notice-text" style={{ color: '#b91c1c' }}>
                      <strong>Cannot send reply.</strong><br/>
                      This user did not provide an email address. You will need to contact them via phone if one was provided.
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="ct-reply-notice">
                      <i className="fa-solid fa-circle-info"></i>
                      <div className="ct-reply-notice-text">
                        <strong>Reply Directly</strong><br/>
                        Your message here will be sent as an email directly to <strong>{selectedContact.email}</strong>.
                      </div>
                    </div>
                    <div className="ct-float-group">
                      <textarea 
                        className="ct-float-textarea" 
                        placeholder=" " 
                        required 
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                      ></textarea>
                      <label className="ct-float-label">Type your reply here...</label>
                    </div>
                  </>
                )}
                
                {/* To push buttons to bottom in flex, the form has a flex column and df can be absolute or we just push it down */}
              </form>
            </div>

            <div className="ct-df">
              {selectedContact && selectedContact.email && (
                  <span className="ct-df-hint" style={{ color: '#64748b' }}>
                    <i className="fa-solid fa-paper-plane" style={{ marginRight: '6px' }}></i>
                    Sending to {selectedContact.email}
                  </span>
              )}
              <div className="ct-df-btns">
                <button type="button" className="ct-btn-cancel" onClick={() => setShowModal(false)}>Close</button>
                {selectedContact && selectedContact.email && (
                  <button type="submit" className="ct-btn-save" onClick={handleReplySubmit} disabled={sendingReply || !replyMessage.trim()}>
                    <i className="fa-solid fa-envelope-circle-check"></i> 
                    {sendingReply ? 'Sending...' : 'Send Reply'}
                  </button>
                )}
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {itemToDelete && (
        <div className="ct-del-overlay" onClick={() => setItemToDelete(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', padding: '28px 24px', borderRadius: '16px', width: '320px', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', animation: 'ct-slide-up 0.2s ease-out' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#fee2e2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', margin: '0 auto 16px' }}>
              <i className="fa-solid fa-triangle-exclamation"></i>
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>Delete Message</h3>
            <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 24px', lineHeight: 1.5 }}>
              Are you sure you want to delete the message from <strong>"{itemToDelete.full_name}"</strong>? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button style={{ flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#f1f5f9', color: '#475569' }} onClick={() => setItemToDelete(null)}>Cancel</button>
              <button style={{ flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#ef4444', color: 'white' }} onClick={executeDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminContacts;
